/**
 * AUViewerEmbed — Thin wrapper around `RegulatoryViewerEmbed`
 * bound to the Australian TGA (PI / CMI) family's API client.
 * See SBAViewerEmbed.tsx / UKPILViewerEmbed.tsx / CAPMViewerEmbed.tsx
 * for the full rationale; same shape applies.
 */
import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { ChunkMetadata, TOCEntry } from '../../types/viewer';
import { auApi } from '../../api/createViewerApi';
import RegulatoryViewerEmbed, {
  type ChunkAnchor,
  type RegulatoryViewerHandle,
} from './RegulatoryViewerEmbed';

export type AUViewerHandle = RegulatoryViewerHandle;

interface AUViewerEmbedProps {
  docId: number;
  highlightChunkIds?: number[];
  chunkAnchors?: Record<number, ChunkAnchor[]>;
  /** page_num -> anchor for a chunk-free (progressive-read) citation.
   *  Forwarded verbatim to RegulatoryViewerEmbed via the props spread. */
  pageAnchors?: Record<number, ChunkAnchor[]> | undefined;
  highlightTerm?: string | null;
  highlightTerms?: string[] | null;
  englishExact?: boolean;
  highlightColors?: (string | undefined)[] | null;
  landingPage?: number | undefined;
  onChunkClick?: (chunk: ChunkMetadata) => void;
  visibleSectionNumbers?: string[] | undefined;
  onTocLoaded?: ((entries: TOCEntry[]) => void) | undefined;
}

const AUViewerEmbed = forwardRef<AUViewerHandle, AUViewerEmbedProps>(
  function AUViewerEmbed(props, ref) {
    const innerRef = useRef<RegulatoryViewerHandle>(null);
    useImperativeHandle(ref, () => ({
      scrollToPage: (n) => innerRef.current?.scrollToPage(n),
      highlightChunks: (ids) => innerRef.current?.highlightChunks(ids),
      scrollToSection: (s, opts) => innerRef.current?.scrollToSection(s, opts) ?? false,
    }));
    return (
      <RegulatoryViewerEmbed
        ref={innerRef}
        viewerApi={auApi}
        {...props}
      />
    );
  },
);

export default AUViewerEmbed;
